import type { ApplyExtractionRequest, ExtractionDetail, ExtractionField, ExtractionItem } from '@/types';
import { formatCalendarDate, formatCurrency, formatMoney } from './formatters';

/**
 * Pure helpers for the extraction review page (#24, spec 6.3): labels, how a canonical value reads,
 * the reviewer's selection and the apply request built from it. The server computes every flag
 * (gap, match, preselect); nothing here decides whether a value may be accepted.
 */

/** Rows decided by the vendor and owner cards, never by a checkbox in the field list */
export const PROPOSAL_FIELDS = new Set(['vendorName', 'customerContactName', 'customerContactEmail']);

const DATE_FIELDS = new Set(['startDate', 'endDate', 'renewalDate', 'invoiceDate', 'dueDate', 'periodStart', 'periodEnd']);
const MONEY_FIELDS = new Set(['totalValue', 'annualValue', 'amount']);

export const FIELD_LABELS: Record<string, string> = {
  vendorName: 'Vendor',
  contractNumber: 'Vendor agreement number',
  title: 'Title',
  startDate: 'Start date',
  endDate: 'End date',
  renewalDate: 'Renewal date',
  autoRenews: 'Auto-renews',
  renewalTermMonths: 'Renewal term (months)',
  noticeDays: 'Notice period (days)',
  totalValue: 'Total value',
  annualValue: 'Annual value',
  currency: 'Currency',
  paymentFrequency: 'Payment frequency',
  paymentTerms: 'Payment terms',
  customerContactName: 'Customer contact',
  customerContactEmail: 'Customer contact email',
  terminationConditions: 'Termination conditions',
  invoiceNumber: 'Invoice number',
  invoiceDate: 'Invoice date',
  dueDate: 'Due date',
  amount: 'Amount',
  periodStart: 'Period start',
  periodEnd: 'Period end',
  purchaseOrder: 'Purchase order',
  product: 'Product',
  quantity: 'Quantity',
  unitCost: 'Unit cost',
  unitCostPeriod: 'Price per',
  licenseType: 'License type',
  topic: 'Topic',
  summary: 'Summary',
};

/** Appended to the contract's notes rather than replacing a value */
export const APPENDED_FIELDS = new Set(['contractNumber', 'terminationConditions']);

export const CLAUSE_TOPICS: Record<string, string> = {
  liability: 'Liability',
  data_protection: 'Data protection',
  price_increase: 'Price increase',
  sla: 'SLA',
  termination_for_convenience: 'Termination for convenience',
  auto_renewal: 'Auto-renewal',
  other: 'Other',
};

export const LICENSE_TYPES = ['PerUser', 'PerSeat', 'Concurrent', 'Site', 'Enterprise', 'ConsumptionBased', 'NamedUser'] as const;

const UNIT_COST_PERIODS: Record<string, string> = {
  month: 'month',
  year: 'year',
  term: 'whole term',
  one_time: 'one-time',
  unknown: 'unknown period',
};

export const fieldLabel = (name: string): string => FIELD_LABELS[name] ?? name;

/**
 * How a canonical value reads on screen. Dates are calendar days (never `new Date(iso)`), money in
 * the given currency, booleans as Yes / No. Everything else is shown as the text it is.
 */
export function displayValue(name: string, value: string | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || value === '') return '';
  if (DATE_FIELDS.has(name)) return formatCalendarDate(value) || value;
  if (MONEY_FIELDS.has(name) || name === 'unitCost') {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return value;
    // Exact amounts: a reviewer compares cents, so never round them away
    try {
      return formatCurrency(amount, { currency: currency?.trim() || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch {
      return formatMoney(amount, currency);
    }
  }
  if (name === 'autoRenews') return value === 'true' ? 'Yes' : value === 'false' ? 'No' : value;
  if (name === 'unitCostPeriod') return UNIT_COST_PERIODS[value] ?? value;
  if (name === 'topic') return CLAUSE_TOPICS[value] ?? value;
  return value;
}

/** A field row's place in the screen's field list: scalar contract or invoice rows the reviewer decides */
export const isDecidableField = (field: ExtractionField): boolean =>
  (field.group === 'contract' || field.group === 'invoice') && field.itemIndex === undefined && !PROPOSAL_FIELDS.has(field.name);

export const itemKey = (item: Pick<ExtractionItem, 'group' | 'itemIndex'>): string => `${item.group}:${item.itemIndex}`;

/** The rows of one item, in property order */
export const itemRows = (detail: ExtractionDetail, item: ExtractionItem): ExtractionField[] =>
  detail.fields.filter((f) => f.group === item.group && f.itemIndex === item.itemIndex);

export interface FieldChoice {
  accepted: boolean;
  /** The reviewer's correction; absent keeps the proposal */
  edited?: string;
}

export interface ItemChoice {
  accepted: boolean;
  edits: Record<string, string>;
}

/**
 * What the reviewer has chosen, bound to the payload it was built from: its id and version tokens
 * travel with the apply, so a reload never carries an old choice onto new data (plan D13a).
 */
export interface ReviewSelection {
  extractionId: string;
  contractVersion: string;
  licenseVersion: string;
  fields: Record<string, FieldChoice>;
  items: Record<string, ItemChoice>;
  vendor: { action: 'keep' | 'select' | 'create'; vendorId?: string; name?: string };
  owner: { action: 'keep' | 'select'; userId?: string };
  statusChange: boolean;
  /** Invoices: the contract to move the document to; never selected for the reviewer */
  moveTo?: string;
}

/** Nothing selected: every acceptance is a click, or the "Accept all above 0.8" shortcut */
export function emptySelection(detail: ExtractionDetail): ReviewSelection {
  return {
    extractionId: detail.id,
    contractVersion: detail.contractVersion,
    licenseVersion: detail.licenseVersion,
    fields: Object.fromEntries(detail.fields.filter(isDecidableField).map((f) => [f.id, { accepted: false }])),
    items: Object.fromEntries(detail.items.map((i) => [itemKey(i), { accepted: false, edits: {} }])),
    vendor: { action: 'keep' },
    owner: { action: 'keep' },
    statusChange: false,
  };
}

/**
 * "Accept all above 0.8": selects exactly the rows and items the server flagged `preselect`
 * (stated, quote verified, confidence at least 0.80, no currency conflict, filling a gap or matching)
 * on top of what is already selected, skipping rows and items the reviewer edited. Vendor, owner and the
 * status change are never selected by it.
 */
export function acceptPreselected(detail: ExtractionDetail, selection: ReviewSelection): ReviewSelection {
  // A row the reviewer edited is left as it is: the flags judge the proposal, not the edit, so an
  // edit is only ever applied by its own checkbox
  const fields = { ...selection.fields };
  for (const field of detail.fields.filter(isDecidableField)) {
    if (field.preselect && !fields[field.id]?.edited?.trim()) fields[field.id] = { ...fields[field.id], accepted: true };
  }
  const items = { ...selection.items };
  for (const item of detail.items) {
    const edited = Object.values(items[itemKey(item)]?.edits ?? {}).some((v) => v.trim() !== '');
    if (item.preselect && !edited) items[itemKey(item)] = { ...items[itemKey(item)], accepted: true };
  }
  return { ...selection, fields, items };
}

export const preselectCount = (detail: ExtractionDetail): number =>
  detail.fields.filter((f) => isDecidableField(f) && f.preselect).length + detail.items.filter((i) => i.preselect).length;

/** Whether the end date is accepted, which offers the Draft to Active proposal on a Draft */
export function endDateAccepted(detail: ExtractionDetail, selection: ReviewSelection): boolean {
  const endDate = detail.fields.find((f) => f.group === 'contract' && f.itemIndex === undefined && f.name === 'endDate');
  return !!endDate && !!selection.fields[endDate.id]?.accepted;
}

/** How many values an apply will write or confirm: the number the confirmation names */
export function selectedCount(detail: ExtractionDetail, selection: ReviewSelection): number {
  const fields = Object.values(selection.fields).filter((f) => f.accepted).length;
  const items = Object.values(selection.items).filter((i) => i.accepted).length;
  const vendor = selection.vendor.action === 'keep' ? 0 : 1;
  const owner = selection.owner.action === 'select' ? 1 : 0;
  const status = selection.statusChange && detail.contractStatus === 'Draft' && endDateAccepted(detail, selection) ? 1 : 0;
  return fields + items + vendor + owner + status + (selection.moveTo ? 1 : 0);
}

const invoiceRow = (detail: ExtractionDetail, name: string) =>
  detail.fields.find((f) => f.group === 'invoice' && f.itemIndex === undefined && f.name === name);

const accepted = (detail: ExtractionDetail, selection: ReviewSelection, name: string): boolean => {
  const row = invoiceRow(detail, name);
  return !!row && !!selection.fields[row.id]?.accepted;
};

/** The contract the invoice's cost record will sit on: the chosen one, else the current one */
export function invoiceTarget(detail: ExtractionDetail, selection: ReviewSelection) {
  const id = selection.moveTo ?? detail.contractId;
  return detail.invoice?.contractProposal?.candidates.find((c) => c.id === id);
}

/** The currency the cost record will carry: the accepted one (edited first), else the destination contract's */
export function costCurrency(detail: ExtractionDetail, selection: ReviewSelection): string | undefined {
  const row = invoiceRow(detail, 'currency');
  if (row && selection.fields[row.id]?.accepted) {
    return (selection.fields[row.id].edited?.trim() || row.value || undefined)?.toUpperCase();
  }
  const target = invoiceTarget(detail, selection);
  return (target ? target.currency : detail.contractCurrency)?.trim() || undefined;
}

/** The invoice states a currency the destination does not use, and it is not accepted: the server would refuse */
export function invoiceCurrencyConflict(detail: ExtractionDetail, selection: ReviewSelection): boolean {
  const stated = detail.extractedCurrency?.trim().toUpperCase();
  const target = invoiceTarget(detail, selection)?.currency?.trim().toUpperCase();
  return !!stated && !!target && stated !== target && accepted(detail, selection, 'amount') && !accepted(detail, selection, 'currency');
}

/** Whether this selection creates a cost record */
export const createsCostRecord = (detail: ExtractionDetail, selection: ReviewSelection): boolean =>
  accepted(detail, selection, 'amount') && accepted(detail, selection, 'invoiceDate');

/** Why an invoice selection cannot be applied as it stands, or null */
export function invoiceBlocker(detail: ExtractionDetail, selection: ReviewSelection): string | null {
  if (detail.schemaType !== 'invoice') return null;
  const hasAmount = accepted(detail, selection, 'amount');
  const hasDate = accepted(detail, selection, 'invoiceDate');
  if (hasAmount !== hasDate) return 'A cost record needs both the amount and the invoice date.';
  const others = Object.values(selection.fields).some((f) => f.accepted);
  if (!hasAmount && others) return 'Accept the amount and the invoice date too: these values go on the cost record.';
  if (invoiceCurrencyConflict(detail, selection)) return 'The invoice is in another currency than this contract: accept the currency too.';
  return null;
}

/** The apply request (spec 6.4): every decidable row and item listed, accepted or rejected */
export function buildApplyRequest(detail: ExtractionDetail, selection: ReviewSelection): ApplyExtractionRequest {
  const fields = detail.fields.filter(isDecidableField).map((field) => {
    const choice = selection.fields[field.id];
    const edited = choice?.edited?.trim();
    return {
      fieldId: field.id,
      decision: choice?.accepted ? ('accept' as const) : ('reject' as const),
      ...(choice?.accepted && edited ? { editedValue: edited } : {}),
    };
  });
  const items = detail.items.map((item) => {
    const choice = selection.items[itemKey(item)];
    const edits = Object.fromEntries(Object.entries(choice?.edits ?? {}).filter(([, v]) => v.trim() !== ''));
    return {
      group: item.group,
      itemIndex: item.itemIndex,
      decision: choice?.accepted ? ('accept' as const) : ('reject' as const),
      ...(choice?.accepted && Object.keys(edits).length > 0 ? { edits } : {}),
    };
  });
  const vendor =
    selection.vendor.action === 'select'
      ? { action: 'select' as const, vendorId: selection.vendor.vendorId }
      : selection.vendor.action === 'create'
        ? { action: 'create' as const, name: selection.vendor.name?.trim() }
        : { action: 'keep' as const };
  const owner =
    selection.owner.action === 'select' ? { action: 'select' as const, userId: selection.owner.userId } : { action: 'keep' as const };
  const statusChange = selection.statusChange && detail.contractStatus === 'Draft' && endDateAccepted(detail, selection) ? 'Active' : null;
  if (detail.schemaType === 'invoice') {
    return {
      contractVersion: selection.contractVersion,
      licenseVersion: selection.licenseVersion,
      fields,
      items: [],
      contract: selection.moveTo ? { action: 'move', contractId: selection.moveTo } : { action: 'keep' },
      knownDuplicates: detail.invoice?.duplicates.map((d) => d.costRecordId) ?? [],
      ...(createsCostRecord(detail, selection) ? { costCurrency: costCurrency(detail, selection) } : {}),
    };
  }
  return {
    contractVersion: selection.contractVersion,
    licenseVersion: selection.licenseVersion,
    fields,
    items,
    vendor,
    owner,
    statusChange,
  };
}

/** Whether a row can be ticked: invalid or missing values need a corrected value first (spec 6.3) */
export function fieldNeedsEdit(field: ExtractionField): boolean {
  return !field.found || !field.isValid;
}

/** The license's product row is missing or invalid, so a name must be typed before accepting */
export const productNeedsEdit = (rows: ExtractionField[]): boolean => {
  const product = rows.find((r) => r.name === 'product');
  return !product || !product.found || !product.isValid;
};

/**
 * Whether a license item can be ticked as it stands, or needs a type, a product name or an annual cost
 * first. A price for the whole term that the live (placeholder) term cannot annualise is computed by the
 * server from accepted start or end dates, so accepting either lifts that blocker; the server still
 * refuses with a clear message when the accepted term is unusable too.
 */
export function licenseBlockers(
  item: ExtractionItem,
  edits: Record<string, string>,
  rows: ExtractionField[] = [],
  termDatesAccepted = false,
): string[] {
  const blockers: string[] = [];
  const period = rows.find((r) => r.name === 'unitCostPeriod')?.value;
  if (item.needsLicenseType && !edits.licenseType) blockers.push('Pick a license type');
  if (item.needsAnnualUnitCost && !edits.annualUnitCost?.trim() && !(period === 'term' && termDatesAccepted)) {
    blockers.push('Enter the annual cost per unit');
  }
  if ((item.duplicateProduct || productNeedsEdit(rows)) && !edits.product?.trim()) {
    blockers.push(item.duplicateProduct ? 'The contract already has this product' : 'Enter the product name');
  }
  return blockers;
}

/** Whether the start or end date is accepted, which changes the term a term price is spread over */
export function termDatesAccepted(detail: ExtractionDetail, selection: ReviewSelection): boolean {
  return detail.fields.some(
    (f) => f.group === 'contract' && f.itemIndex === undefined && (f.name === 'startDate' || f.name === 'endDate') && !!selection.fields[f.id]?.accepted,
  );
}
